import type { DestinationEvent } from '@eventbrite/consumer-search.event-renderer';
import { AlertOutlineIcon, Button, InlineAlert, Modal } from '@eventbrite/marmalade';
import Image from 'next/image';
import { defineMessages, useIntl } from 'react-intl';

import replacementArtwork from '../../../../public/images/featured-event-replace.png';

import styles from './FeaturedEvent.module.scss';

const messages = defineMessages({
  close: {
    id: 'Close highlighted event confirmation',
    defaultMessage: 'Close highlighted event confirmation',
  },
  eyebrow: {
    id: 'CONFIRM CHANGE',
    defaultMessage: 'CONFIRM CHANGE',
  },
  title: {
    id: 'Replace your highlighted event?',
    defaultMessage: 'Replace your highlighted event?',
  },
  description: {
    id: 'This event becomes your highlight, and the current one goes back to being a regular event.',
    defaultMessage:
      'This event becomes your highlight, and the current one goes back to being a regular event.',
  },
  replace: {
    id: 'Replace event',
    defaultMessage: 'Replace event',
  },
  keep: {
    id: 'Keep current event',
    defaultMessage: 'Keep current event',
  },
  error: {
    id: 'We could not replace your highlighted event. Please try again.',
    defaultMessage: 'We could not replace your highlighted event. Please try again.',
  },
});

interface Props {
  candidate: DestinationEvent | null;
  isPending: boolean;
  hasError: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function ReplaceFeaturedEventModal({
  candidate,
  isPending,
  hasError,
  onClose,
  onConfirm,
}: Props) {
  const intl = useIntl();
  if (!candidate) return null;

  return (
    <Modal
      isShown
      headerTitle={intl.formatMessage(messages.title)}
      handleClose={onClose}
      closeTitle={intl.formatMessage(messages.close)}
      className={styles.replaceModal}
    >
      <div className={styles.replaceModalBody}>
        <div className={styles.modalArtwork} aria-hidden>
          <Image
            src={replacementArtwork}
            alt=""
            width={1510}
            height={1199}
            className={styles.modalArtworkImage}
            unoptimized
          />
        </div>
        <div className={styles.modalContent} aria-busy={isPending}>
          <p className={styles.modalEyebrow}>{intl.formatMessage(messages.eyebrow)}</p>
          <h2 className={styles.modalTitle}>{intl.formatMessage(messages.title)}</h2>
          <p className={styles.modalDescription}>{intl.formatMessage(messages.description)}</p>
          {hasError ? (
            <InlineAlert
              variant="danger"
              icon={<AlertOutlineIcon aria-hidden />}
              description={intl.formatMessage(messages.error)}
              className={styles.modalError}
            />
          ) : null}
          <div className={styles.modalActions}>
            <Button
              variant="primary"
              disabled={isPending}
              onClick={onConfirm}
              className={styles.modalAction}
            >
              {intl.formatMessage(messages.replace)}
            </Button>
            <Button
              variant="secondary"
              disabled={isPending}
              onClick={onClose}
              className={styles.modalAction}
            >
              {intl.formatMessage(messages.keep)}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
